import Component from '@glimmer/component'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { isNone } from '@ember/utils'
import { subMonths, isBefore } from 'date-fns'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type { IntlService } from 'ember-intl'
import type Teacher from 'teacher-ui-client/models/teacher'
import type School from 'teacher-ui-client/models/school'
import type SubscriptionUtilisation from 'teacher-ui-client/models/subscription-utilisation'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import { PageHeader } from 'teacher-ui-client/components/page-header'
import { t } from 'ember-intl'
import { concat } from '@ember/helper'
import { StartButton } from 'teacher-ui-client/components/start-button'
import { Product } from 'teacher-ui-client/utils/products'
import {
  InlineAlert,
  AlertType,
  InlineAlertTitle,
  InlineAlertSubtitle,
  InlineAlertActionsContainer,
} from 'teacher-ui-client/components/inline-alert'
import { LinkTo } from '@ember/routing'
import { on } from '@ember/modifier'
import { ManageStudents } from 'teacher-ui-client/components/home/tiles/manage-students'
import { HomeTilesManageAssignmentsReading } from 'teacher-ui-client/components/home/tiles/manage-assignments/reading'
import { HomeTilesManageAssignmentsMaths } from 'teacher-ui-client/components/home/tiles/manage-assignments/maths'
import { RecentWriting } from 'teacher-ui-client/components/home/tiles/recent-writing'
import { ManageSubscription } from 'teacher-ui-client/components/home/tiles/manage-subscription'
import { HomeTilesTeacherToolkit } from 'teacher-ui-client/components/home/tiles/teacher-toolkit'
import { ReportsWriting } from 'teacher-ui-client/components/home/tiles/reports/writing'
import { ReportsReading } from 'teacher-ui-client/components/home/tiles/reports/reading'
import { ReportsMaths } from 'teacher-ui-client/components/home/tiles/reports/maths'
import { WritingIdeas } from 'teacher-ui-client/components/home/tiles/writing-ideas'
import { PromoVideos } from 'teacher-ui-client/components/writing-legends/promo-videos'
import { HomeTilesVideo } from 'teacher-ui-client/components/home/tiles/video'
import { or } from 'ember-truth-helpers'
import type { UIScope } from 'teacher-ui-client/reporting/ui-scope/route'
import { MSUpsell } from 'teacher-ui-client/components/promo/ms-upsell'
import { NoStandardsSetAlert } from 'teacher-ui-client/components/standards-set/no-standards-set-alert'
import { canAccess } from 'teacher-ui-client/helpers/can-access'
import { MSFreeAccessCrossSell } from 'teacher-ui-client/components/promo/ms-free-access-crosssell'
import { ReFreeAccessCrossSell } from 'teacher-ui-client/components/promo/re-free-access-crosssell'
import CreateAssignmentsTile from 'teacher-ui-client/components/home/tiles/homeschool/create-assignments'
import FamilyDashboardTile from 'teacher-ui-client/components/home/tiles/homeschool/family-dashboard'
import LessonResourcesTile from 'teacher-ui-client/components/home/tiles/homeschool/lesson-resources'
import ReportingTile from 'teacher-ui-client/components/home/tiles/homeschool/reporting'
import TeacherLibraryTile from 'teacher-ui-client/components/home/tiles/homeschool/teacher-library'
import CertificatesTile from 'teacher-ui-client/components/home/tiles/homeschool/certificates'
import HomeschoolTilesVideo from 'teacher-ui-client/components/home/tiles/homeschool/video'
import TeacherHomeBanner from 'teacher-ui-client/components/home/teacher-home-banner'

interface Args {
  currentSchool?: School
  currentTeacher?: Teacher
  subscriptionType: SubscriptionType
  scopeName?: UIScope['scope']
  scopeId?: UIScope['id']
  utilisation?: SubscriptionUtilisation
  isSubco?: boolean
  subcoForSubscription?: Teacher
  skipEmailConfirmation?: boolean
  showClassLogins?: boolean
  homeschoolMaxVideoUrl?: string
}

interface Signature {
  Args: Args
  Element: HTMLDivElement
}

interface Theme {
  taglineTextColor?: string
}

const themes: Record<SubscriptionType, Theme> = {
  [SubscriptionType.Reading]: {
    taglineTextColor: 'text-oceany-blue-325',
  },
  [SubscriptionType.Maths]: {
    taglineTextColor: 'text-ms-green-325',
  },
  [SubscriptionType.Writing]: {
    taglineTextColor: 'text-wl-blue-300',
  },
}

const isWritingSubscription = (subscriptionType: SubscriptionType): subscriptionType is SubscriptionType.Writing =>
  subscriptionType === SubscriptionType.Writing

function isHomeschoolReadingOrMathsSubscription(
  subscriptionType: SubscriptionType,
  isHomeSchool: boolean,
): subscriptionType is SubscriptionType.Reading | SubscriptionType.Maths {
  return isHomeSchool && (subscriptionType === SubscriptionType.Reading || subscriptionType === SubscriptionType.Maths)
}

export class HomePage extends Component<Signature> {
  @service intl!: IntlService

  @service flashQueue!: FlashQueueService

  get countryCode() {
    return this.args.currentSchool?.countryCode ?? 'US'
  }

  get isHomeSchool(): boolean {
    return !!this.args.currentSchool?.isHomeschool
  }

  get isOneRosterEnabled() {
    return Boolean(this.args.currentSchool?.oneRosterEnabledAt)
  }

  get isCountryCodeAu() {
    return this.countryCode === 'AU'
  }

  get isReadingSubscription() {
    return this.args.subscriptionType === SubscriptionType.Reading
  }

  get isMathsSubscription() {
    return this.args.subscriptionType === SubscriptionType.Maths
  }

  get isWritingSubscription() {
    return this.args.subscriptionType === SubscriptionType.Writing
  }

  // returns something like ['school', 123] or ['teacher', 456]
  get reportingDashboardModels() {
    return [this.args.scopeName, this.args.scopeId]
  }

  get selectedTheme() {
    return themes[this.args.subscriptionType]
  }

  get showEmailConfirmationAlert() {
    const confirmedAt = this.args.currentTeacher?.emailConfirmedAt
    const sixMonthsAgo = subMonths(new Date(), 6)
    return !this.args.skipEmailConfirmation && (isNone(confirmedAt) || isBefore(confirmedAt, sixMonthsAgo))
  }

  get showNoStandardsSetAlert() {
    const currentSchool = this.args.currentSchool
    if (!currentSchool) return false
    // there are no standards-set aligned writing content, so avoid showing the alert for writing subscriptions
    return (
      !currentSchool.standardsSet &&
      !currentSchool.isHomeschool &&
      this.args.subscriptionType !== SubscriptionType.Writing
    )
  }

  @action
  async confirmEmail() {
    const teacher = this.args.currentTeacher
    try {
      if (!teacher) throw new Error('current teacher undefined when saving confirmed at')
      teacher.emailConfirmedAt = new Date()
      await teacher.save()
      this.flashQueue.addSuccess({ subtitle: this.intl.t('pages.home.alerts.confirmEmail.flash.confirmEmailSuccess') })
    } catch {
      teacher?.rollbackAttributes() // undo the confirmed date, so that the alert comes back
      this.flashQueue.addFail({ subtitle: this.intl.t('pages.home.alerts.confirmEmail.flash.confirmEmailFail') })
    }
  }

  <template>
    <div class="space-y-6" ...attributes>
      <PageHeader>
        <:title>
          <h1>
            {{t "pages.home.welcomeMessage" name=@currentTeacher.fullName}}
          </h1>
        </:title>
      </PageHeader>

      <h3 class="mt-6 text-center italic {{this.selectedTheme.taglineTextColor}}">
        {{t (concat "pages.home.tagline." @subscriptionType)}}
      </h3>

      {{#if @showClassLogins}}
        <div class="flex flex-wrap justify-center" data-test-start-buttons>
          {{#if this.isReadingSubscription}}
            <StartButton class="mx-3 mb-3" @product={{Product.RE}} @showABCBranding={{this.isCountryCodeAu}} />
            <StartButton class="mx-3 mb-3" @product={{Product.REX}} @showABCBranding={{this.isCountryCodeAu}} />
            <StartButton class="mx-3 mb-3" @product={{Product.FP}} />
          {{else if this.isMathsSubscription}}
            <StartButton class="mx-3 mb-3" @product={{Product.MS}} @showABCBranding={{this.isCountryCodeAu}} />
            {{#if (canAccess "msp_product")}}
              <StartButton class="mx-3 mb-3" @product={{Product.MSP}} />
            {{/if}}
          {{else if this.isWritingSubscription}}
            <StartButton class="mx-3 mb-3" @product={{Product.WL}} />
          {{/if}}
        </div>
      {{/if}}

      <div class="mx-auto max-w-(--breakpoint-xl) space-y-4">

        <TeacherHomeBanner />

        <MSUpsell />
        {{#if this.isReadingSubscription}}
          <MSFreeAccessCrossSell @isSubco={{Boolean @isSubco}} />
        {{/if}}
        {{#if this.isMathsSubscription}}
          <ReFreeAccessCrossSell @isSubco={{Boolean @isSubco}} />
        {{/if}}
        {{#if this.showEmailConfirmationAlert}}
          <InlineAlert data-test-confirm-email @type={{AlertType.Warning}}>
            <InlineAlertTitle>
              {{t "pages.home.alerts.confirmEmail.header"}}
            </InlineAlertTitle>
            <InlineAlertSubtitle>
              <p>
                {{t "pages.home.alerts.confirmEmail.updateMsg" email=@currentTeacher.email htmlSafe=true}}
              </p>
              <p>
                {{t "pages.home.alerts.confirmEmail.confirmMsg"}}
              </p>

            </InlineAlertSubtitle>
            <InlineAlertActionsContainer>
              <button type="button" {{on "click" this.confirmEmail}} class="text-blue-325 hover:text-blue-350">
                {{t "pages.home.alerts.confirmEmail.confirmEmail"}}
              </button>
              <LinkTo @route="subscription-type.account" class="text-blue-325 hover:text-blue-350">
                {{t "pages.home.alerts.confirmEmail.updateEmail"}}
              </LinkTo>
            </InlineAlertActionsContainer>
          </InlineAlert>
        {{/if}}
      </div>

      {{#if this.showNoStandardsSetAlert}}
        <div class="mx-auto max-w-(--breakpoint-xl)" data-test-no-standards-set>
          <NoStandardsSetAlert
            @hasDisco={{Boolean @currentSchool.district.districtCoordinators.length}}
            @isSubco={{@isSubco}}
            @isOneRosterEnabled={{this.isOneRosterEnabled}}
          />
        </div>
      {{/if}}

      <div class="mx-auto grid max-w-(--breakpoint-xl) grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 xl:gap-8">
        {{#if (isHomeschoolReadingOrMathsSubscription @subscriptionType this.isHomeSchool)}}
          {{! Tile 1 }}
          <CreateAssignmentsTile @subscriptionType={{@subscriptionType}} />

          {{! Tile 2 }}
          <ReportingTile @subscriptionType={{@subscriptionType}} @uiScopeModels={{this.reportingDashboardModels}} />

          {{! Tile 3 }}
          <LessonResourcesTile @subscriptionType={{@subscriptionType}} />

          {{! Tile 4 }}
          {{#if this.isReadingSubscription}}
            <TeacherLibraryTile />
          {{else if this.isMathsSubscription}}
            <CertificatesTile />
          {{/if}}

          {{! Tile 5 }}
          <FamilyDashboardTile />

          <HomeschoolTilesVideo @src={{@homeschoolMaxVideoUrl}} />
        {{else}}
          {{! Tile 1 }}
          <ManageStudents @subscriptionType={{@subscriptionType}} />

          {{! Tile 2 }}
          {{#if this.isReadingSubscription}}
            <HomeTilesManageAssignmentsReading />
          {{else if this.isMathsSubscription}}
            <HomeTilesManageAssignmentsMaths />
          {{else if this.isWritingSubscription}}
            <RecentWriting @reportingDashboardModels={{this.reportingDashboardModels}} />
          {{/if}}

          {{! Tile 3 }}
          <ManageSubscription
            @school={{@currentSchool}}
            @utilisation={{@utilisation}}
            @subscriptionType={{@subscriptionType}}
            @isSubco={{@isSubco}}
            @subcoForSubscription={{@subcoForSubscription}}
          />

          {{! Tile 4 }}
          {{#if (or this.isReadingSubscription this.isMathsSubscription)}}
            <HomeTilesTeacherToolkit @subscriptionType={{@subscriptionType}} />
          {{else if this.isWritingSubscription}}
            <ReportsWriting @reportingDashboardModels={{this.reportingDashboardModels}} />
          {{/if}}

          {{! Tile 5 }}
          {{#if this.isReadingSubscription}}
            <ReportsReading @reportingDashboardModels={{this.reportingDashboardModels}} />
          {{else if this.isMathsSubscription}}
            <ReportsMaths
              @reportingDashboardModels={{this.reportingDashboardModels}}
              @showPrimeReports={{canAccess "msp_product"}}
            />
          {{else if this.isWritingSubscription}}
            <WritingIdeas />
          {{/if}}

          {{! Tile 6 }}
          {{#if (isWritingSubscription @subscriptionType)}}
            <PromoVideos />
          {{else}}
            <HomeTilesVideo @subscriptionType={{@subscriptionType}} />
          {{/if}}
        {{/if}}
      </div>
    </div>
  </template>
}

export default HomePage
